import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useAuthUser } from './useAuthUser';
import { createReadSequence } from '@/utils/readSequence';
import { useServices } from '@/infrastructure/providers/ServiceProvider';
import type { Services } from '@/infrastructure/services';
import { ensureMyOrg } from '@/infrastructure/clients/BackendClient';
import { isSignUpOpen } from './environmentPreferencesRead';
import { onAccountChanges } from '@/modules/mcpStream';
import { OrgInstance, OrgMember } from '@/types/orgTypes';

interface UseOrganizationResult {
  orgInstance: OrgInstance | null;
  orgMember: OrgMember | null;
  subscriptionPlan: string | null;
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  saveImmersalToken: (token: string) => Promise<boolean>;
}

const OrganizationContext = createContext<UseOrganizationResult | null>(null);

const RETRY_MS = [2_000, 5_000, 15_000, 30_000, 60_000];

const ENSURE_TRIED_KEY = 'lureo_org_ensure_tried';
const ensureTried = new Set<string>();

function ensureTriedFor(userId: string): boolean {
  if (ensureTried.has(userId)) return true;
  try {
    return sessionStorage.getItem(`${ENSURE_TRIED_KEY}:${userId}`) !== null;
  } catch {
    return false;
  }
}

function markEnsureTried(userId: string, tried: boolean) {
  if (tried) ensureTried.add(userId);
  else ensureTried.delete(userId);
  try {
    if (tried) sessionStorage.setItem(`${ENSURE_TRIED_KEY}:${userId}`, '1');
    else sessionStorage.removeItem(`${ENSURE_TRIED_KEY}:${userId}`);
  } catch {
    // Storage blocked: remembered for this page load only
  }
}

// The membership and its org in one round trip: the org is read as the caller's own (getMyOrg), so it needs no read of
// the membership first
const readMembership = (organization: Services['organization'], userId: string) =>
  Promise.all([organization.fetchOrgMember(userId), organization.fetchMyOrgInstance()]);

async function readOrg(organization: Services['organization'], userId: string) {
  let [member, instance] = await readMembership(organization, userId);
  // In no org: a first sign-in whose provisioning failed is provisioned here, once per tab
  if (!member && !ensureTriedFor(userId) && (await isSignUpOpen())) {
    markEnsureTried(userId, true);
    const { error } = await ensureMyOrg();
    if (error) {
      // A setup that failed is tried again by the next read, Try again included
      markEnsureTried(userId, false);
      throw error;
    }
    [member, instance] = await readMembership(organization, userId);
  }
  return { member, instance: member ? instance : null };
}

/**
 * Provider that fetches the current user's org member + org instance and shares the result with all consumers via context.
 *
 * Must live inside both AuthProvider and ServiceProvider.
 */
export const OrganizationProvider = ({ children }: { children: ReactNode }) => {
  const { user, loading: authLoading } = useAuthUser();
  const services = useServices();
  const userId = user?.id;
  const [orgInstance, setOrgInstance] = useState<OrgInstance | null>(null);
  const [orgMember, setOrgMember] = useState<OrgMember | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [failures, setFailures] = useState(0);
  // Each read's number: a reply to an older read (another user's, or one a newer read replaced) is dropped
  const [reads] = useState(createReadSequence);

  // Another user starts from nothing, so the previous user's org is never shown (or sent to analytics) as theirs.
  // Wait for auth to resolve before deciding logged-out vs logged-in; otherwise we'd flip loading=false on the pre-auth
  // render and callers like AdminProtectedRoute would see a null subscriptionPlan and redirect.
  const [startedFor, setStartedFor] = useState<{ userId?: string; authLoading: boolean } | null>(null);
  if (!startedFor || startedFor.userId !== userId || startedFor.authLoading !== authLoading) {
    setStartedFor({ userId, authLoading });
    if (!authLoading) {
      if (startedFor?.userId !== userId) {
        setOrgInstance(null);
        setOrgMember(null);
      }
      setError(null);
      setFailures(0);
      setLoading(Boolean(userId));
    }
  }

  const loadOrg = useCallback((id: string) => {
    const read = reads.next();
    return readOrg(services.organization, id)
      .then(({ member, instance }) => {
        if (!reads.isLatest(read)) return;
        setOrgMember(member ?? null);
        setOrgInstance(instance);
        setError(null);
        setFailures(0);
      })
      .catch((err) => {
        if (!reads.isLatest(read)) return;
        const error = err instanceof Error ? err : new Error('Failed to fetch organization instance');
        console.error('Error fetching organization instance:', error);
        setError(error);
        setFailures(n => n + 1);
      })
      .finally(() => {
        if (reads.isLatest(read)) setLoading(false);
      });
  }, [services.organization, reads]);

  useEffect(() => {
    if (authLoading || !userId) {
      reads.next();
      return;
    }
    void loadOrg(userId);
  }, [authLoading, userId, loadOrg, reads]);

  useEffect(() => {
    if (!userId || !error || failures === 0 || failures > RETRY_MS.length) return;
    const timer = setTimeout(() => void loadOrg(userId), RETRY_MS[failures - 1]);
    return () => clearTimeout(timer);
  }, [userId, error, failures, loadOrg]);

  // Retries that ran out pick up again when the person comes back to the tab or the network returns
  useEffect(() => {
    if (!userId || !error) return;
    const again = () => {
      if (document.visibilityState === 'visible') void loadOrg(userId);
    };
    window.addEventListener('online', again);
    document.addEventListener('visibilitychange', again);
    return () => {
      window.removeEventListener('online', again);
      document.removeEventListener('visibilitychange', again);
    };
  }, [userId, error, loadOrg]);

  // An empty batch means the stream reopened after a gap
  useEffect(() => {
    if (!userId) return;
    return onAccountChanges(['org', 'orgMember'], events => {
      if (events.length > 0 && !events.some(event => event.entity === 'org' || event.entityUniqueId === userId)) return;
      void loadOrg(userId);
    });
  }, [userId, loadOrg]);

  const refresh = useCallback(async () => {
    if (authLoading || !userId) return;
    setLoading(true);
    await loadOrg(userId);
  }, [authLoading, userId, loadOrg]);

  const saveImmersalToken = useCallback(async (token: string): Promise<boolean> => {
    if (!userId) throw new Error('Not authenticated');
    await services.organization.updateOrgInstanceImmersalToken(userId, token);
    const read = reads.next();
    try {
      const fresh = await services.organization.fetchOrgInstanceByUserId(userId);
      if (reads.isLatest(read)) setOrgInstance(fresh ?? null);
      return Boolean(fresh?.orgInstanceImmersalConfigured);
    } catch {
      // Saved; only the read back failed
      void loadOrg(userId);
      return token.trim() !== '';
    }
  }, [userId, services.organization, loadOrg, reads]);

  const subscriptionPlan = orgInstance?.orgInstanceSubscriptionPlan || null;

  // Memoize so consumers don't re-render when the provider re-renders for
  // unrelated reasons (matches the pattern used by other providers).
  const value = useMemo<UseOrganizationResult>(
    () => ({
      orgInstance,
      orgMember,
      subscriptionPlan,
      loading,
      error,
      refresh,
      saveImmersalToken,
    }),
    [orgInstance, orgMember, subscriptionPlan, loading, error, refresh, saveImmersalToken]
  );

  return createElement(OrganizationContext.Provider, { value }, children);
};

/**
 * Hook to read the current user's organization instance, org member, and
 * subscription plan. All consumers share a single fetch via OrganizationProvider.
 */
export const useOrganization = (): UseOrganizationResult => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
};
